import { lazy, Suspense, useEffect, useRef, useState, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import Layout from "./components/Layout";
import useStore from "./store/useStore";
import { supabase } from "./lib/supabase";
import { C } from "./theme";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./lib/msalConfig";
import { TeamMemberContext } from "./context/TeamMemberContext";
import { loadTeamPermissions, getMemberByEmail } from "./lib/teamPermissions";

// Lazy-loaded pages — each loads its own JS chunk on demand, keeping the initial bundle small
const Dashboard            = lazy(() => import("./pages/Dashboard"));
const Clients              = lazy(() => import("./pages/Clients"));
const Tasks                = lazy(() => import("./pages/Tasks"));
const Deadlines            = lazy(() => import("./pages/Deadlines"));
const Documents            = lazy(() => import("./pages/Documents"));
const Compliance           = lazy(() => import("./pages/Compliance"));
const Bookings             = lazy(() => import("./pages/Bookings"));
const Team                 = lazy(() => import("./pages/Team"));
const Settings             = lazy(() => import("./pages/Settings"));
const PIScorePage          = lazy(() => import("./pages/PIScorePage"));
const ClientAcceptancePage = lazy(() => import("./pages/ClientAcceptance"));
const CalendarPage         = lazy(() => import("./pages/Calendar"));
const Login                = lazy(() => import("./pages/Login"));
const Services             = lazy(() => import("./pages/Services"));
const Templates            = lazy(() => import("./pages/Templates"));
const Communications       = lazy(() => import("./pages/Communications"));
const Calculators          = lazy(() => import("./pages/Calculators"));
const SignedDocuments      = lazy(() => import("./pages/SignedDocuments"));
const NotificationsPage    = lazy(() => import("./pages/NotificationsPage"));
const ClientLogin          = lazy(() => import("./pages/ClientLogin"));
const ClientSetup          = lazy(() => import("./pages/ClientSetup"));
const ClientPortal         = lazy(() => import("./pages/ClientPortal"));
const SigningPage           = lazy(() => import("./pages/SigningPage"));
const CopyPassword         = lazy(() => import("./pages/CopyPassword"));
const ApprovePermissions   = lazy(() => import("./pages/ApprovePermissions"));
const LearningHub          = lazy(() => import("./pages/LearningHub"));
const LearningHubApp       = lazy(() => import("./pages/LearningHubApp"));
const TermsAndConditions   = lazy(() => import("./pages/TermsAndConditions"));
const StoreLogin           = lazy(() => import("./pages/StoreLogin"));
const StorePortal          = lazy(() => import("./pages/StorePortal"));

// ── Public routes — rendered before practice auth check ──────────
const PUBLIC_ROUTES = ["/client-login", "/client-setup", "/portal", "/sign/", "/copy-password", "/approve-permissions", "/terms", "/store-login", "/store-login/", "/store"];
function isPublicPath() {
  const p = window.location.pathname;
  return PUBLIC_ROUTES.some(r => p === r || p.startsWith(r));
}

// ── Learning Hub hostname detection ──────────────────────────────
function isLearningHubDomain() {
  const h = window.location.hostname;
  return h === "learn.mdgaccounting.co.za" || h === "learn.mdgaccounting.co.za" || h === "learn.mdgaccounting.netlify.app";
}

const PAGE_TITLES = {
  "/":                  "MDG Accounting | Practice Portal",
  "/clients":           "MDG Accounting | Clients",
  "/tasks":             "MDG Accounting | Tasks",
  "/deadlines":         "MDG Accounting | Deadlines",
  "/documents":         "MDG Accounting | Documents",
  "/compliance":        "MDG Accounting | Compliance",
  "/bookings":          "MDG Accounting | Bookings",
  "/settings":          "MDG Accounting | Settings",
  "/team":              "MDG Accounting | Team",
  "/planner":           "MDG Accounting | Planner",
  "/services":          "MDG Accounting | Services",
  "/templates":         "MDG Accounting | Templates",
  "/communications":    "MDG Accounting | Communications",
  "/piscore":           "MDG Accounting | PI Score",
  "/calculators":       "MDG Accounting | Calculators",
  "/signed-documents":  "MDG Accounting | Signed Documents",
  "/learning-hub":      "MDG Accounting | Learning Hub",
  "/portal":            "MDG Accounting | Client Portal",
  "/client-login":      "MDG Accounting | Client Login",
};

function TitleManager() {
  const { pathname } = window.location;
  useEffect(() => {
    const handler = () => {
      document.title = PAGE_TITLES[window.location.pathname] || "MDG Accounting";
    };
    handler();
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [pathname]);
  return null;
}

function ClientPortalApp() {
  // Version poller — forces reload when a new deploy is detected (critical for iOS PWAs)
  useEffect(() => {
    const check = () => {
      fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
        .then((r) => r.json())
        .then((data) => {
          const stored = localStorage.getItem('_app_ver');
          if (!stored) {
            localStorage.setItem('_app_ver', data.v);
          } else if (stored !== data.v && !sessionStorage.getItem('_ver_reloading')) {
            localStorage.setItem('_app_ver', data.v);
            sessionStorage.setItem('_ver_reloading', '1');
            setTimeout(() => sessionStorage.removeItem('_ver_reloading'), 15000);
            window.location.reload();
          }
        })
        .catch(() => {});
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <ChunkErrorBoundary>
      <BrowserRouter>
        <TitleManager />
        <Toaster position="top-right" />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/client-login"  element={<ClientLogin />} />
            <Route path="/client-setup"  element={<ClientSetup />} />
            <Route path="/portal"        element={<ClientPortal />} />
            <Route path="/sign/:token"   element={<SigningPage />} />
            <Route path="/copy-password" element={<CopyPassword />} />
            <Route path="/approve-permissions" element={<ApprovePermissions />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/store-login" element={<StoreLogin />} />
            <Route path="/store-login/:storeCode" element={<StoreLogin />} />
            <Route path="/store/:storeCode" element={<StorePortal />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ChunkErrorBoundary>
  );
}

// Forces a full-page reload when a practice user navigates to a client portal path.
// App() only checks isPublicPath() on initial render; internal React Router navigation
// doesn't re-invoke App(), so the practice router would show NotFound without this.
function PublicPathReload() {
  useEffect(() => { window.location.href = window.location.pathname + window.location.search + window.location.hash; }, []);
  return null;
}

// Catches chunk-load failures that happen when a new deployment removes old bundle files
class ChunkErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) {
    const isChunkError = error?.name === "ChunkLoadError" ||
      /loading chunk|failed to fetch dynamically imported|Loading CSS chunk/i.test(error?.message || "");
    if (isChunkError && !sessionStorage.getItem("_chunk_reload")) {
      sessionStorage.setItem("_chunk_reload", "1");
      window.location.reload();
    }
  }
  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#fff", gap: 12, fontFamily: "'Open Sans','Segoe UI',sans-serif", padding: 24 }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#991B1B" }}>Something went wrong</div>
          <div style={{ fontSize: 12, color: "#7F1D1D", fontFamily: "monospace", wordBreak: "break-all", maxWidth: 480, textAlign: "center", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px" }}>{msg}</div>
          <button onClick={() => { this.setState({ error: null }); }} style={{ marginTop: 8, padding: "8px 20px", background: "#008C95", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function LoadingScreen() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.bg, gap: 14, fontFamily: "'Open Sans','Segoe UI',sans-serif" }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: C.orange, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 28px rgba(0,140,149,0.35)", overflow: "hidden" }}>
        <img src="/mdg-logo-white.png" alt="MDG Accounting" style={{ width: 42, height: 42, objectFit: "contain" }} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em" }}>MDG Accounting</div>
      <div style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>Loading your practice data…</div>
      <div style={{ width: 200, height: 3, background: C.slateD, borderRadius: 10, overflow: "hidden", marginTop: 8 }}>
        <div style={{ width: "55%", height: 3, background: C.orange, borderRadius: 10, animation: "loading-bar 1.1s ease-in-out infinite alternate" }} />
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 12, fontFamily: "'Open Sans','Segoe UI',sans-serif" }}>
      <div style={{ fontSize: 48 }}>🔍</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>Page not found</div>
      <div style={{ fontSize: 13, color: C.textSub }}>The page you're looking for doesn't exist.</div>
      <a href="/" style={{ marginTop: 8, padding: "8px 20px", background: C.orange, color: "#fff", borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>← Back to Dashboard</a>
    </div>
  );
}

function ErrorScreen({ message, onRetry }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.bg, gap: 12 }}>
      <div style={{ fontSize: 32 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.red }}>Could not connect to database</div>
      <div style={{ fontSize: 13, color: C.textSub, maxWidth: 400, textAlign: "center" }}>{message}</div>
      <button onClick={onRetry} style={{ marginTop: 8, padding: "8px 20px", background: C.orange, color: C.white, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Retry</button>
    </div>
  );
}

// ── Force-change-password screen (team members, first login) ─────────────────
function ForceChangePassword({ session, teamMember, onDone }) {
  const [pwd,  setPwd]  = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pwd !== pwd2)   { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      const res = await fetch("/.netlify/functions/team-member-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token || ""}` },
        body: JSON.stringify({ action: "clear_must_change", authUserId: teamMember.authUserId, password: pwd }),
      });
      const json = await res.json();
      if (!json.ok) { setErr(json.error || "Failed to update password. Please try again."); return; }
      // Clear the flag locally and allow the app to continue
      onDone({ ...teamMember, mustChangePassword: false });
    } catch (e) {
      setErr(e.message || "Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f4f7f9", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Open Sans','Segoe UI',sans-serif", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.10)", width: "100%", maxWidth: 440, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ background: "#005F63", padding: "28px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: 0.3 }}>MDG Accounting</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", marginTop: 4 }}>Practice Portal</div>
        </div>
        {/* Body */}
        <div style={{ padding: "32px 32px 28px" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#005F63", marginBottom: 8 }}>Set Your New Password</div>
          <div style={{ fontSize: 13, color: "#4a5568", lineHeight: 1.6, marginBottom: 24 }}>
            Welcome, <strong>{teamMember.name || teamMember.email}</strong>! For your security, please create a new personal password before continuing.
          </div>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#718096", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>New Password</label>
              <input
                type="password"
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                placeholder="Min 8 characters"
                required
                style={{ width: "100%", fontSize: 13, padding: "10px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#718096", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Confirm New Password</label>
              <input
                type="password"
                value={pwd2}
                onChange={e => setPwd2(e.target.value)}
                placeholder="Re-enter your new password"
                required
                style={{ width: "100%", fontSize: 13, padding: "10px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
              />
            </div>
            {err && (
              <div style={{ fontSize: 12, color: "#C53030", background: "#FFF5F5", border: "1px solid #FEB2B2", borderRadius: 8, padding: "10px 12px" }}>
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              style={{ marginTop: 4, padding: "12px", background: busy ? "#a0aec0" : "#005F63", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", transition: "background 0.2s" }}
            >
              {busy ? "Setting password…" : "Set Password & Continue →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Team Member Dashboard — shown at "/" for team members only ───────────────
function TeamMemberDashboard({ teamMember }) {
  const allClients = useStore(s => s.clients);
  const bc = "#005F63";
  const assignedClients = allClients.filter(c =>
    (teamMember?.clientIds || []).map(String).includes(String(c.id))
  );

  return (
    <div style={{ padding: "28px 24px", fontFamily: "'Open Sans','Segoe UI',sans-serif", maxWidth: 900 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: bc, marginBottom: 4 }}>
          Welcome, {teamMember?.name || "Team Member"}
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          You have access to {assignedClients.length} client{assignedClients.length !== 1 ? "s" : ""}. Click a client below to open their profile.
        </div>
      </div>

      {/* Quick links */}
      <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
        <a href="/clients" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", background: bc, color: "#fff", borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
          👥 All Clients
        </a>
        <a href="/planner" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#f1f5f9", color: bc, border: `1.5px solid ${bc}`, borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
          🗓️ Planner
        </a>
      </div>

      {/* Assigned client cards */}
      {assignedClients.length === 0 ? (
        <div style={{ padding: 32, background: "#f8fafc", borderRadius: 12, textAlign: "center", color: "#64748b", fontSize: 14 }}>
          No clients have been assigned to you yet. Contact your practice manager.
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#475569", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Assigned Clients</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {assignedClients.map(c => {
              const tabPerms = teamMember?.tabs?.[String(c.id)] || {};
              const allowedTabs = ["Overview","Communications","Tasks","Deadlines","Planner","Documents","Accounting","Payroll","Sales Orders","Purchasing","Back Office","Tax & Compliance","Xero","Video Call","Store Admin"]
                .filter(t => tabPerms[t] !== false);
              return (
                <a key={c.id} href={`/clients?open=${c.id}`}
                  style={{ display: "block", background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", textDecoration: "none", color: "inherit", transition: "box-shadow 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,95,99,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: bc, marginBottom: 4 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{allowedTabs.length} section{allowedTabs.length !== 1 ? "s" : ""} accessible</div>
                  {allowedTabs.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                      {allowedTabs.slice(0, 4).map(t => (
                        <span key={t} style={{ fontSize: 10, padding: "2px 7px", background: "#f0fdf4", color: "#15803d", borderRadius: 99, fontWeight: 600 }}>{t}</span>
                      ))}
                      {allowedTabs.length > 4 && (
                        <span style={{ fontSize: 10, padding: "2px 7px", background: "#f1f5f9", color: "#64748b", borderRadius: 99, fontWeight: 600 }}>+{allowedTabs.length - 4} more</span>
                      )}
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Practice App (all hooks live here, never rendered on portal paths) ──
function PracticeApp() {
  const loadAll = useStore((s) => s.loadAll);
  const loading = useStore((s) => s.loading);
  const error   = useStore((s) => s.error);

  // ── Auth state ──
  const [session,     setSession]     = useState(undefined); // undefined = checking
  const [authChecked, setAuthChecked] = useState(false);
  const [teamMemberCtx, setTeamMemberCtx] = useState(null); // null = owner

  useEffect(() => {
    supabase.auth.getSession()
      .then(({ data }) => { setSession(data.session); })
      .catch(() => { setSession(null); })
      .finally(() => { setAuthChecked(true); });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  // After session is known, check if this user is a team member
  useEffect(() => {
    if (!session?.user?.email) { setTeamMemberCtx(null); return; }
    loadTeamPermissions().then(permData => {
      const member = getMemberByEmail(permData, session.user.email);
      if (member) {
        setTeamMemberCtx({ ...member, __isTeamMember: true });
      } else {
        setTeamMemberCtx(null); // owner
      }
    }).catch(() => setTeamMemberCtx(null));
  }, [session]);

  // Poll version.json every 60s — shows a toast when a new deployment is detected.
  // The index.html inline script already handles the hard reload on initial page load;
  // this poller only notifies the user while they are actively using the app so they
  // can choose when to reload rather than having the page yanked away under them.
  useEffect(() => {
    let toastShown = false;
    const check = () => {
      fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
        .then((r) => r.json())
        .then((data) => {
          const stored = localStorage.getItem('_app_ver');
          if (!stored) {
            localStorage.setItem('_app_ver', data.v);
          } else if (stored !== data.v && !toastShown) {
            toastShown = true;
            localStorage.setItem('_app_ver', data.v);
            toast(
              (t) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>App updated — tap to refresh</span>
                  <button
                    onClick={() => { toast.dismiss(t.id); window.location.reload(); }}
                    style={{ background: '#008C95', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Refresh
                  </button>
                </span>
              ),
              { duration: Infinity, id: 'app-update' }
            );
          }
        })
        .catch(() => {});
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, []);

  const dataLoaded = useRef(false);
  useEffect(() => {
    if (session && !dataLoaded.current) {
      dataLoaded.current = true;
      loadAll();
    }
    if (!session) dataLoaded.current = false; // reset on logout
  }, [session, loadAll]);

  // ── Google OAuth callback handler ────────────────────────────────
  // Google redirects back to mdgaccounting.co.za with ?code=xxx&state=google_oauth
  // We exchange the code server-side and store the refresh_token in Supabase.
  useEffect(() => {
    if (!session) return; // must be logged in
    const params = new URLSearchParams(window.location.search);
    const code  = params.get("code");
    const state = params.get("state");
    if (code && state === "google_oauth") {
      // Clean up URL immediately so the code can't be re-used
      window.history.replaceState({}, document.title, window.location.pathname);
      fetch("/.netlify/functions/google-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "exchange", code }),
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.ok) {
            localStorage.setItem("google_connected", "1");
            toast.success(`Google Calendar connected${d.email ? ` — ${d.email}` : ""}! You can now create Google Meet video calls.`, { duration: 6000 });
          } else {
            toast.error(`Google connect failed: ${d.error || "Unknown error"}`);
          }
        })
        .catch(() => toast.error("Google connect failed — please try again"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Still checking auth
  if (!authChecked) return <LoadingScreen />;

  // Not logged in → show login
  if (!session) return <><Toaster position="top-right" /><Suspense fallback={<LoadingScreen />}><Login /></Suspense></>;

  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen message={error} onRetry={loadAll} />;

  // ── Force password change for team members on first login ──────────────────
  if (teamMemberCtx?.__isTeamMember && teamMemberCtx?.mustChangePassword) {
    return <ForceChangePassword session={session} teamMember={teamMemberCtx} onDone={(updated) => setTeamMemberCtx(updated)} />;
  }

  return (
    <TeamMemberContext.Provider value={teamMemberCtx}>
    <ChunkErrorBoundary>
    <BrowserRouter>
      <TitleManager />
      <Toaster position="top-right" />
      <Layout>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/planner" element={<CalendarPage />} />
            {/* Routes below redirect team members to /clients */}
            <Route path="/tasks" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Tasks />} />
            <Route path="/deadlines" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Deadlines />} />
            <Route path="/documents" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Documents />} />
            <Route path="/compliance" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Compliance />} />
            <Route path="/bookings" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Bookings />} />
            <Route path="/team" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Team />} />
            <Route path="/settings" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Settings />} />
            <Route path="/piscore" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <PIScorePage />} />
            <Route path="/acceptance" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <ClientAcceptancePage />} />
            <Route path="/services" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Services />} />
            <Route path="/templates" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Templates />} />
            <Route path="/communications" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Communications />} />
            <Route path="/calculators" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <Calculators />} />
            <Route path="/signed-documents" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <SignedDocuments />} />
            <Route path="/notifications" element={teamMemberCtx?.__isTeamMember ? <Navigate to="/clients" replace /> : <NotificationsPage />} />
            <Route path="/learning-hub" element={<LearningHub />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            {/* Public/portal paths — force full-page reload so App() re-evaluates isPublicPath() */}
            <Route path="/portal" element={<PublicPathReload />} />
            <Route path="/client-login" element={<PublicPathReload />} />
            <Route path="/client-setup" element={<PublicPathReload />} />
            <Route path="/store-login" element={<PublicPathReload />} />
            <Route path="/store/*" element={<PublicPathReload />} />
            <Route path="/sign/*" element={<PublicPathReload />} />
            <Route path="/copy-password" element={<PublicPathReload />} />
            <Route path="/approve-permissions" element={<PublicPathReload />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </BrowserRouter>
    </ChunkErrorBoundary>
    </TeamMemberContext.Provider>
  );
}

// Handles both normal app load and popup-callback context (window.opener set after auth redirect).
// In popup mode: initialize() processes the auth code and notifies the parent via BroadcastChannel,
// then we close the popup. The parent's loginPopup() promise resolves and useIsAuthenticated() → true.
function MsalGate() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    msalInstance.initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then(() => setReady(true))
      .catch(() => setReady(true));
  }, []);

  if (!ready) return <LoadingScreen />;
  return <MsalProvider instance={msalInstance}><PracticeApp /></MsalProvider>;
}

// ── Root router — decides between Learning Hub, client portal, or practice app ──
export default function App() {
  if (isLearningHubDomain()) {
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <LearningHubApp />
        </Suspense>
      </ChunkErrorBoundary>
    );
  }
  if (isPublicPath()) return <ClientPortalApp />;
  return <MsalGate />;
}
